import * as geom from "js/core/utilities/geom";
import { BaseElement } from "js/elements/base/BaseElement";
import { SVGPathElement } from "js/elements/base/SVGElement";
import { Shape } from "js/elements/base/Shapes";
import { layoutHelper } from "js/elements/layouts/LayoutHelper";
import { AnimationSequenceType } from "common/constants";

class RadialBarGraph extends BaseElement {
    get currentValue() {
        if (this.canvas.isAnimating) {
            return this.model.value * this.animationState.value;
        }

        return this.model.value;
    }

    get labelText() {
        return this.getRootElement().formatValue(this.currentValue);
    }

    _build() {
        this.circle = this.addElement("circle", () => SVGPathElement);
        this.path = this.addElement("path", () => SVGPathElement);

        if (this.options.showLabel) {
            this.label = this.addElement("label", () => this.options.labelElement, {
                model: {
                    label: this.labelText
                },
                constrainWidth: true,
                scaleTextToFit: true,
                canEdit: false
            });
        }
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        let radius = Math.min(size.width, size.height) / 2 - Math.max(styles.radialCircle.strokeWidth, styles.radialPath.strokeWidth) / 2;

        children.setProps(this.circle, {
            path: Shape.drawArc(radius, radius, radius, 0.001, 2 * Math.PI).toPathData(),
            styles: styles.radialCircle
        });

        let value = this.options.valueFunction();
        if (this.canvas.isAnimating) {
            value = value * this.animationState.value;
        }

        let startAngle = 0 - 90 / 180 * Math.PI;
        let angle = Math.min(359.99, 360 * value);
        let endAngle = (angle - 90) / 180 * Math.PI;

        children.setProps(this.path, {
            path: Shape.drawArc(radius, radius, radius, startAngle, endAngle).toPathData(),
            styles: styles.radialPath
        });

        if (this.options.showLabel) {
            if (this.canvas.isAnimating) {
                this.label.updateText(this.labelText);
            }

            let labelProps = children.setProps(this.label, new geom.Size(radius * 2, radius * 2));
            labelProps.bounds = new geom.Rect(0, 0, labelProps.size);
            // labelProps.bounds = new geom.Rect(layoutHelper.getAlignOffset(labelProps.size, new geom.Size(radius * 2, radius * 2)), labelProps.size);
        }

        return { size: new geom.Size(radius * 2, radius * 2) };
    }

    get buildAnimations() {
        let anims = [{
            type: AnimationSequenceType.ANIMATE_TO_VALUE,
            target: this,
            delay: 0,
            startValue: 0,
            endValue: 1,
            easing: "easeOutQuad",
            prepare: () => {
                this.animationState = { value: 0 };
            },
            callback: value => {
                this.animationState = { value };
            },
            reset: () => {
                this.animationState = { value: 1 };
            },
        }, {
            type: AnimationSequenceType.FADE,
            target: this.path.DOMNode,
            delay: 0
        }, {
            type: AnimationSequenceType.FADE,
            target: this.circle.DOMNode,
            delay: 0
        }];

        if (this.label) {
            anims.push({
                type: AnimationSequenceType.FADE,
                target: this.label.DOMNode,
                delay: 100
            });
        }

        return anims;
    }
}

export { RadialBarGraph };
